import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Printer, Share2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PrescriptionView({ open, onOpenChange, prescription, patient }) {
  const [clinic, setClinic] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadClinic = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const { data } = await api.get("/clinic/settings");
      setClinic(data);
    } catch (e) {
      // silently fall back; printable still shows basics
    } finally {
      setLoading(false);
    }
  }, [open]);

  useEffect(() => { loadClinic(); }, [loadClinic]);

  if (!prescription) return null;

  const printNow = () => {
    const el = document.getElementById("rx-printable-area");
    if (!el) return;
    const w = window.open("", "_blank", "width=900,height=1000");
    if (!w) {
      toast.error("Allow pop-ups to print");
      return;
    }
    w.document.write(`<!doctype html><html><head><title>Prescription - ${prescription.patient_name || ""}</title>
      <style>
        * { box-sizing: border-box; }
        body { font-family: 'Inter', -apple-system, system-ui, sans-serif; margin: 24px; color: #0f172a; }
        h1 { margin: 0 0 4px 0; font-size: 24px; }
        h2 { margin: 16px 0 6px; font-size: 16px; text-transform: uppercase; letter-spacing: 0.05em; color: #64748b; font-weight: 700; }
        .header { border-bottom: 3px solid #2563eb; padding-bottom: 12px; margin-bottom: 18px; display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; }
        .header .info { font-size: 12px; color: #475569; line-height: 1.5; }
        .meta { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; font-size: 13px; margin-bottom: 16px; }
        .meta strong { color: #0f172a; }
        .meta .label { font-size: 10px; text-transform: uppercase; letter-spacing: 0.06em; color: #94a3b8; }
        table { width: 100%; border-collapse: collapse; font-size: 13px; margin-top: 8px; }
        th { text-align: left; background: #f1f5f9; padding: 8px 10px; font-size: 11px; text-transform: uppercase; letter-spacing: 0.05em; color: #475569; border-bottom: 2px solid #cbd5e1; }
        td { padding: 10px; border-bottom: 1px solid #e2e8f0; vertical-align: top; }
        .Rx { font-family: 'Times New Roman', serif; font-size: 32px; font-weight: 700; color: #2563eb; margin-right: 8px; }
        .notes { margin-top: 18px; padding: 12px; background: #f8fafc; border-left: 3px solid #2563eb; font-size: 13px; color: #334155; }
        .signature { margin-top: 48px; text-align: right; font-size: 13px; color: #475569; }
        .signature .line { border-top: 1px solid #cbd5e1; width: 200px; margin-left: auto; padding-top: 4px; }
        .footer { margin-top: 32px; padding-top: 12px; border-top: 1px solid #e2e8f0; font-size: 10px; color: #94a3b8; text-align: center; }
        @media print { body { margin: 0; } }
      </style></head><body>${el.innerHTML}</body></html>`);
    w.document.close();
    setTimeout(() => { w.focus(); w.print(); }, 250);
  };

  const sharedText = () => {
    const lines = [];
    lines.push(`*Prescription* — ${clinic?.name || "Clinic"}`);
    lines.push(`Patient: ${prescription.patient_name}`);
    lines.push(`Date: ${prescription.date}`);
    lines.push(`Doctor: ${prescription.doctor_name}`);
    if (prescription.diagnosis) lines.push(`Diagnosis: ${prescription.diagnosis}`);
    lines.push("");
    lines.push("*Medicines:*");
    (prescription.medicines || []).forEach((m, i) => {
      const parts = [`${i + 1}. ${m.name}`];
      if (m.dosage) parts.push(m.dosage);
      if (m.frequency) parts.push(m.frequency);
      if (m.duration) parts.push(`for ${m.duration}`);
      if (m.instructions) parts.push(`(${m.instructions})`);
      lines.push(parts.join(" — "));
    });
    if (prescription.notes) {
      lines.push("");
      lines.push(`Notes: ${prescription.notes}`);
    }
    lines.push("");
    lines.push(`— Sent via HeDesk`);
    return lines.join("\n");
  };

  const shareWhatsApp = () => {
    const phone = (patient?.phone || "").replace(/\D/g, "");
    const text = encodeURIComponent(sharedText());
    const url = phone ? `https://wa.me/${phone}?text=${text}` : `https://wa.me/?text=${text}`;
    window.open(url, "_blank");
  };

  const meds = prescription.medicines || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="prescription-view-dialog" className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Manrope" }}>Prescription preview</DialogTitle>
        </DialogHeader>

        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <Button onClick={printNow} variant="outline" data-testid="rx-print-btn">
            <Printer className="w-4 h-4 mr-2" /> Print
          </Button>
          <Button onClick={shareWhatsApp} className="bg-emerald-600 hover:bg-emerald-700" data-testid="rx-whatsapp-btn">
            <Share2 className="w-4 h-4 mr-2" /> Share on WhatsApp
          </Button>
          {loading && <Loader2 className="w-4 h-4 animate-spin text-slate-400 ml-2" />}
        </div>

        {/* Printable area */}
        <div id="rx-printable-area" className="bg-white p-6 rounded-lg border border-slate-200">
          <div className="header" style={{ borderBottom: "3px solid #2563eb", paddingBottom: 12, marginBottom: 18, display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16 }}>
            <div>
              <h1 style={{ margin: "0 0 4px 0", fontSize: 24 }}>{clinic?.name || "Clinic"}</h1>
              <div className="info" style={{ fontSize: 12, color: "#475569", lineHeight: 1.5 }}>
                {clinic?.address && <div>{clinic.address}</div>}
                {clinic?.phone && <div>Phone: {clinic.phone}</div>}
                {clinic?.license_number && <div>Reg / License: {clinic.license_number}</div>}
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <span className="Rx" style={{ fontFamily: "Times New Roman, serif", fontSize: 32, fontWeight: 700, color: "#2563eb" }}>℞</span>
            </div>
          </div>

          <div className="meta" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, fontSize: 13, marginBottom: 16 }}>
            <div>
              <div className="label" style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.06em", color: "#94a3b8" }}>Patient</div>
              <div><strong>{prescription.patient_name}</strong></div>
              {patient?.age && patient?.gender && <div style={{ color: "#64748b" }}>{patient.age}y · {patient.gender}</div>}
              {patient?.phone && <div style={{ color: "#64748b" }}>{patient.phone}</div>}
            </div>
            <div>
              <div className="label" style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.06em", color: "#94a3b8" }}>Date</div>
              <div><strong>{prescription.date}</strong></div>
              <div className="label" style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.06em", color: "#94a3b8", marginTop: 6 }}>Doctor</div>
              <div><strong>{prescription.doctor_name}</strong></div>
            </div>
          </div>

          {prescription.diagnosis && (
            <>
              <h2 style={{ margin: "16px 0 6px", fontSize: 13, textTransform: "uppercase", letterSpacing: "0.05em", color: "#64748b", fontWeight: 700 }}>Diagnosis</h2>
              <div style={{ fontSize: 14, color: "#0f172a" }}>{prescription.diagnosis}</div>
            </>
          )}

          <h2 style={{ margin: "16px 0 6px", fontSize: 13, textTransform: "uppercase", letterSpacing: "0.05em", color: "#64748b", fontWeight: 700 }}>Medicines</h2>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, marginTop: 8 }}>
            <thead>
              <tr>
                <th style={{ textAlign: "left", background: "#f1f5f9", padding: "8px 10px", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", color: "#475569", borderBottom: "2px solid #cbd5e1", width: "32px" }}>#</th>
                <th style={{ textAlign: "left", background: "#f1f5f9", padding: "8px 10px", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", color: "#475569", borderBottom: "2px solid #cbd5e1" }}>Medicine</th>
                <th style={{ textAlign: "left", background: "#f1f5f9", padding: "8px 10px", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", color: "#475569", borderBottom: "2px solid #cbd5e1" }}>Dosage</th>
                <th style={{ textAlign: "left", background: "#f1f5f9", padding: "8px 10px", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", color: "#475569", borderBottom: "2px solid #cbd5e1" }}>Freq.</th>
                <th style={{ textAlign: "left", background: "#f1f5f9", padding: "8px 10px", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", color: "#475569", borderBottom: "2px solid #cbd5e1" }}>Duration</th>
                <th style={{ textAlign: "left", background: "#f1f5f9", padding: "8px 10px", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", color: "#475569", borderBottom: "2px solid #cbd5e1" }}>Instructions</th>
              </tr>
            </thead>
            <tbody>
              {meds.map((m, i) => (
                <tr key={i}>
                  <td style={{ padding: 10, borderBottom: "1px solid #e2e8f0", color: "#64748b" }}>{i + 1}</td>
                  <td style={{ padding: 10, borderBottom: "1px solid #e2e8f0", fontWeight: 600 }}>{m.name}</td>
                  <td style={{ padding: 10, borderBottom: "1px solid #e2e8f0" }}>{m.dosage || "—"}</td>
                  <td style={{ padding: 10, borderBottom: "1px solid #e2e8f0" }}>{m.frequency || "—"}</td>
                  <td style={{ padding: 10, borderBottom: "1px solid #e2e8f0" }}>{m.duration || "—"}</td>
                  <td style={{ padding: 10, borderBottom: "1px solid #e2e8f0" }}>{m.instructions || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {prescription.notes && (
            <div className="notes" style={{ marginTop: 18, padding: 12, background: "#f8fafc", borderLeft: "3px solid #2563eb", fontSize: 13, color: "#334155" }}>
              <strong>Notes:</strong> {prescription.notes}
            </div>
          )}

          <div className="signature" style={{ marginTop: 48, textAlign: "right", fontSize: 13, color: "#475569" }}>
            <div className="line" style={{ borderTop: "1px solid #cbd5e1", width: 200, marginLeft: "auto", paddingTop: 4 }}>
              {prescription.doctor_name}
            </div>
          </div>

          <div className="footer" style={{ marginTop: 32, paddingTop: 12, borderTop: "1px solid #e2e8f0", fontSize: 10, color: "#94a3b8", textAlign: "center" }}>
            Generated by HeDesk · This is a computer-generated prescription
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
